// CLI v0.8 — entity-target resolver for entity-targeted verbs.
//
// Every M8b mutating-ops verb (rollback, pull, ssh, env/*, secrets/*)
// targets exactly one server-side entity. The verb has two ways of
// learning which one:
//
//   1. The user passes `[<name>]` on the command line — the slug or
//      entity_id of an entity in the active org.
//   2. The verb is run inside a directory linked via `.cloudgrid/link.json`.
//
// Both paths converge on a single shape: the resolver returns the entity_id
// (used to address v2 endpoints) and the entity name (kept on the target
// for display; pull/ssh/rollback now resolve through the v2 surface, so
// the historical `/apps/:name/*` callers are gone).
//
// The active-org pre-check is a clamp on the link path: if the user is
// inside a linked dir whose org disagrees with the active org, the spec v17
// §5 mismatch error fires *before* any HTTP call. Out-of-folder lookups
// (path 1) skip the §5 check by design — the user supplied the target
// explicitly, and the server-side `resolveRuntime` will scope by active org
// either way (UUID-leak guard).

import type { LinkArtifact } from './link.js';
import { requireLink, validateLinkVsActiveOrg } from './link.js';
import { getActiveOrg } from '../active-org.js';
import { CloudGridError } from '../utils.js';
import type { IV2Client } from '../api/v2-client.js';
import { isGlobalEntityRef } from './entity-ref.js';
import { resolveBareSlugAcrossOrgs, bareSlugNotFoundMessage } from './resolve-bare-slug.js';

/**
 * The minimal entity shape every M8b verb needs. Returned by both the
 * link path (resolved via `GET /api/v2/runtimes/:entity_id`) and the
 * out-of-folder path (resolved via `GET /api/v2/runtimes/:name`).
 */
export interface EntityTarget {
  entity_id: string;
  /** Yaml slug — matches the entity's K8s namespace and display URL. */
  name: string;
  display_name: string;
  org_slug: string;
  /** Whether the resolution came from `.cloudgrid/link.json` or `[<name>]`. */
  source: 'link' | 'arg';
}

/** Path to the v2 endpoint that returns the canonical entity detail. */
const RUNTIMES_BASE = '/api/v2/runtimes';

export interface ResolveEntityTargetOpts {
  /**
   * ADR 036 V2 — skip the link-vs-active-org pre-check (and its
   * `getActiveOrg` round-trip) on the link path. The link's `entity_id` is
   * a UUID, which the server now resolves globally by membership in the
   * entity's home org, so read verbs (rollback, pull) work from a linked
   * dir regardless of the CLI's active org. Verbs whose server endpoints
   * are still active-org-gated (unplug, ssh) keep the default `false`.
   */
  skipLinkOrgCheck?: boolean;
  /**
   * CLI 0.9.3 — when true, a bare-slug `nameArg` (no `org/` prefix, no UUID)
   * is resolved across every org the caller belongs to, instead of being
   * passed straight to the server to scope to the active org. Opt-in so only
   * the read verbs that adopted it (rollback, pull) change behavior; unplug,
   * ssh, env/*, secrets/* keep their active-org-scoped resolution.
   */
  bareSlugAcrossOrgs?: boolean;
  /**
   * Verb name used to word the "not found in any of your orgs" message on the
   * bare-slug arg path (e.g. `cloudgrid <verb> org/<slug>`). Only consulted
   * when `bareSlugAcrossOrgs` is set. Defaults to `clone`.
   */
  verb?: string;
}

/**
 * Resolve a verb's target entity.
 *
 * Behaviour matches spec v17 §13.5–§13.9 and §5:
 *  - If `nameArg` is provided, ignore link.json entirely and look up by
 *    name/UUID. (`<name>` overrides; the user told us where to look.)
 *  - Otherwise read `.cloudgrid/link.json`. Compare its `org_id` to the
 *    active org; mismatch → throws `LinkOrgMismatchError` — unless the
 *    caller passes `skipLinkOrgCheck` (ADR 036 V2 read verbs).
 *  - Either way, `GET /api/v2/runtimes/:idOrName` is the source of truth
 *    for the entity name/display_name; we never reconstruct it locally.
 *
 * Throws:
 *  - `CloudGridError` from `getActiveOrg` if no active org is configured
 *    or the configured slug is no longer accessible (spec §23 wording).
 *  - `V2ApiError(404, 'RUNTIME_NOT_FOUND')` if the v2 lookup 404s.
 *  - `V2ApiError(401, 'AUTH_REQUIRED')` if the JWT is missing/expired
 *    (bubbles up from the underlying `getOrgs()` call).
 *  - `LinkOrgMismatchError` from `validateLinkVsActiveOrg` (spec §5).
 *  - `MalformedLinkError` from `requireLink` if link.json is corrupt.
 */
export async function resolveEntityTarget(
  client: IV2Client,
  nameArg: string | undefined,
  cwd: string = process.cwd(),
  opts: ResolveEntityTargetOpts = {},
): Promise<EntityTarget> {
  if (nameArg && nameArg.trim().length > 0) {
    const arg = nameArg.trim();
    // CLI 0.9.3 (opt-in, rollback + pull): a bare slug (no `org/` prefix, no
    // UUID) resolves across every org you belong to, instead of scoping
    // silently to the active org. A unique match is addressed by its UUID
    // below; a UUID or `org/slug` ref is already globally addressable and
    // passes straight through, as does any arg when the opt is off.
    let idToFetch = arg;
    if (opts.bareSlugAcrossOrgs && !isGlobalEntityRef(arg)) {
      const match = await resolveBareSlugAcrossOrgs(arg, client);
      if (!match) {
        throw new CloudGridError(bareSlugNotFoundMessage(arg, opts.verb ?? 'clone'));
      }
      idToFetch = match.entity_id;
    }
    const detail = await fetchEntityDetail(client, idToFetch);
    return {
      entity_id: detail.entity_id,
      name: detail.name,
      display_name: detail.display_name,
      org_slug: detail.org_slug,
      source: 'arg',
    };
  }

  const link: LinkArtifact = requireLink(cwd);

  if (!opts.skipLinkOrgCheck) {
    const activeOrg = await getActiveOrg(client);
    validateLinkVsActiveOrg(link, activeOrg);
  }

  const detail = await fetchEntityDetail(client, link.entity_id);
  return {
    entity_id: detail.entity_id,
    name: detail.name,
    display_name: detail.display_name,
    org_slug: detail.org_slug,
    source: 'link',
  };
}

interface RuntimeDetail {
  entity_id: string;
  name: string;
  display_name: string;
  org_slug: string;
}

async function fetchEntityDetail(
  client: IV2Client,
  idOrName: string,
): Promise<RuntimeDetail> {
  const resp = await client.get<{
    entity_id: string;
    name: string;
    display_name: string;
    org: { slug: string };
  }>(`${RUNTIMES_BASE}/${encodeURIComponent(idOrName)}`);
  return {
    entity_id: resp.entity_id,
    name: resp.name,
    display_name: resp.display_name,
    org_slug: resp.org.slug,
  };
}
